import styles from './Events.module.css';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import EmojiEvents from '@mui/icons-material/EmojiEvents';
import Typography from '@mui/material/Typography';

import React from 'react';

import { DateTime } from 'luxon';

import { useAuth } from '../../AuthContext';

import { Event } from '../Event/Event';
import { Shifts } from '../Shifts/Shifts';
import { Roles } from '../Roles/Roles';

const EventHeader = ({ onNewEvent, readOnly }) => {
    const { auth } = useAuth();

    const handleNewEvent = () => {
        onNewEvent({
            type: '',
            nameShort: '',
            nameLong: '',
            description: '',
            location: '',
            attendance: '',
            limit: '',
            organizer: { name: auth.currentUser.displayName, email: auth.currentUser.email },
            closes: '',
            opens: '',
            start: '',
            end: '',
            earlyStart: '',
            earlyEnd: '',
            earlyRewardAllShifts: false,
            earlyRewardPerShift: false,
            timezone: DateTime.now().zoneName
        });
    };

    return (
        <div className={ styles.header }>
            <Typography variant="title">All Events</Typography>

            <Button onClick={ handleNewEvent } sx={{ display: readOnly ? 'none' : 'flex' }} size="small" variant="outlined" color="primary" startIcon={ <Icon component={ Add } /> }>
                Add New Event
            </Button>
        </div>
    );
};

const EventList = ({ events, newEvent, onSaveEvent, onDeleteEvent, onCancel, readOnly = true }) => {
    const [ expanded, setExpanded ] = React.useState(false);

    const { auth, claims } = useAuth();

    const handleOnSaveEvent = async (event) => {
        setExpanded(true);

        await onSaveEvent(event);
    };

    return (
        <div className={ `${ styles.events } ${ expanded ? styles.expanded : '' }` }>
            { newEvent && <Event event={ newEvent } onSaveEvent={ handleOnSaveEvent } onCancel={ onCancel } readOnly={ readOnly } /> }

            { events.map(e => (
                <EventListItem
                    key={ e.id }
                    event={ e }
                    newEvent={ newEvent }
                    onSaveEvent={ handleOnSaveEvent }
                    onDeleteEvent={ onDeleteEvent }
                    readOnly={ readOnly || !(!!claims?.admin || (!!claims?.scheduler && e.organizer?.email === auth.currentUser.email)) }
                />
            )) }
        </div>
    );
};

const EventListItem = ({ event, onSaveEvent, onDeleteEvent, readOnly }) => {
    const [ editEvent, setEditEvent ] = React.useState(false);
    const [ roles, setRoles ] = React.useState([]);

    const handleOnCancel = () => setEditEvent(false);

    const handleShowEdit = () => {
        if (editEvent) {
            setEditEvent(false);
        } else {
            setEditEvent(true);
        }
    }

    const handleOnSaveEvent = (event) => {
        onSaveEvent(event);
    };

    return (
        <div className={ styles.eventCard }>
            <div className={ `${ styles.item } ${ styles.eventHeader } ${ editEvent ? styles.expanded : '' }` } style={{ cursor: !readOnly ? 'pointer' : 'default' }}>
                <Grid container spacing={ 1 } alignItems="center" sx={{ width: '100%' }} onClick={ !readOnly ? handleShowEdit : undefined }>
                    <Grid size={ 6 }>
                        <Box display='flex' alignItems='center'>
                            <Typography variant='h5' sx={ { mr: 1 } }>{ event.nameShort || event.nameLong }</Typography>
                            
                            <Tooltip title={ event.earlyRewardAllShifts ? "Early reward for all shifts enabled" : event.earlyRewardPerShift ? "Early reward per shift enabled" : "" }>
                                <Icon
                                    component={ EmojiEvents }
                                    color='warning'
                                    sx={ {
                                        display: event.earlyRewardAllShifts || event.earlyRewardPerShift ? 'flex' : 'none',
                                        verticalAlign: 'middle',
                                        mr: 0.5
                                    } } />
                            </Tooltip>
                        </Box>
                    </Grid>

                    <Grid size={ 2 }>
                        <Typography variant='h5'>{ DateTime.fromISO(event.start).toFormat('MMM d') }</Typography>
                    </Grid>

                    <Grid size={ 2 }>
                        <Typography variant='h5'>{ DateTime.fromISO(event.end).toFormat('MMM d') }</Typography>
                    </Grid>

                    <Grid size={ 2 } display="flex" justifyContent="flex-end">
                        <IconButton onClick={ (e) => { e.stopPropagation(); handleShowEdit(); } } sx={{ display: readOnly ? 'none' : 'flex' }} size="small" color={ editEvent ? 'secondary' : 'primary' } variant="outlined">
                            <Icon component={ editEvent ? Close : Edit } />
                        </IconButton>
                    </Grid>
                </Grid>
            </div>

            { editEvent && !readOnly &&
                <div className={ styles.expanded }>
                    <Event event={ event } onSaveEvent={ handleOnSaveEvent } onDeleteEvent={ onDeleteEvent } onCancel={ handleOnCancel } readOnly={ false } />
                    
                    <Roles type={ event.type } onRolesLoaded={ setRoles } />
                    
                    { roles.length > 0 && <Shifts event={ event } roles={ roles } /> }
                </div>
            }

            { editEvent && readOnly &&
                <div className={ styles.expanded }>
                    <Event event={ event } readOnly={ true } />
                    
                    <Roles type={ event.type } onRolesLoaded={ setRoles } readOnly={ true } />

                    { roles.length > 0 && <Shifts event={ event } roles={ roles } readOnly={ true } /> }
                </div>
            }
        </div>
    );
};

export const Events = ({ events, readOnly = true, onSaveEvent, onDeleteEvent }) => {
    const [ newEvent, setNewEvent ] = React.useState(null);

    const sorted = events.sort((a, b) => DateTime.fromISO(a.start) - DateTime.fromISO(b.start));
    const filtered = sorted.filter(e => e.end && DateTime.fromISO(e.end) >= DateTime.now().minus({ days: 1 }));

    const handleOnSaveEvent = (event) => {
        onSaveEvent(event);
    };

    return (
        <div className={ styles.eventContainer }>
            <EventHeader onNewEvent={ setNewEvent } readOnly={ readOnly } />

            { filtered.length > 0 || newEvent ? <EventList events={ filtered } newEvent={ newEvent } readOnly={ readOnly } onSaveEvent={ handleOnSaveEvent } onDeleteEvent={ onDeleteEvent } onCancel={ () => setNewEvent(null) } /> : <div className={ styles.events }>No events found</div> }
        </div>
    );
};